import {
	isNull,
	isObjectLiteral,
	removeUndefinedValues,
} from "../core/helpers/object-util.js";
import AlertCollector from "../core/alerts/AlertCollector.js";

/**
 * Argument passed to a `validate` function.
 *
 * @typedef {object} ValidateArgument
 * @property {AlertCollector} collector -
 * @property {object} context -
 */

//
// This class is a rewrite of model.js using ES classes.
//

/**
 * `model` is the abstract superclass of all models.
 *
 * @abstract
 * @param {{}} spec - Parameter object
 * @param {{}} my - protected fields holder
 * @return {*} model
 */
class Model {
	/**
	 * Default implementation. Returns all properties on `my` serialized.
	 *
	 * @return {object} Literal representation of the model
	 */
	serialize() {
		// we use clone() to remove the keys whose values are undefined/.
		const schema = removeUndefinedValues(this._serializedProperties());

		return this._serializeProperties(schema);
	}

	/**
	 * Default `toString` implementation for models.
	 *
	 * @return {string} string representation of the instance.
	 */
	toString() {
		return JSON.stringify(
			this._serializeProperties(this._serializedProperties()),
		);
	}

	isModel() {
		return true;
	}

	isValid(context) {
		return !this.validation(context).hasError();
	}

	/**
	 * Method validating a model by appending the errors/warnings to the
	 * provided collector.
	 *
	 * @param {ValidateArgument} param - Collector to gather validation
	 *   errors/warnings, and a context to provide validation data
	 */
	validate({ collector: _collector, context: _context }) {
		// valid by default
	}

	validation(context) {
		return new AlertCollector().updateValidationResults({
			model: this,
			context,
		});
	}

	match(query) {
		const allQueries = query.split(" ");

		return allQueries.every((singleQuery) =>
			this._matchSingleQuery(singleQuery),
		);
	}

	_matchSingleQuery(query) {
		const lowerCaseQuery = query.toLowerCase();

		return this._getMatchingFields().some((field) =>
			field?.toLowerCase().includes(lowerCaseQuery),
		);
	}

	_getMatchingFields() {
		return [];
	}

	_subclassResponsibility() {
		return this.constructor._subclassResponsibility();
	}

	_serializedProperties() {
		return {};
	}

	_serializeProperties(schema) {
		const properties = {};

		if (!isObjectLiteral(schema)) {
			return this._serializeProperty(schema);
		}

		Object.keys(schema).forEach((key) => {
			const property = schema[key];
			if (property === undefined || property === null) {
				properties[key] = property;
				return;
			}

			// Exclude protected methods
			if (typeof property === "function") {
				return;
			}

			if (property.constructor === Array) {
				// eslint-disable-next-line no-shadow -- bad practice, we should fix this
				properties[key] = property.map((property) =>
					this._serializeProperty(property),
				);

				return;
			}

			if (!property.isModel && isObjectLiteral(property)) {
				properties[key] = this._serializeProperties(property);
				return;
			}

			properties[key] = this._serializeProperty(property);
		});

		return properties;
	}

	_serializeProperty(property) {
		if (property === null || property === undefined) {
			return property;
		}

		if (property.isModel && property.isModel()) {
			return property.serialize();
		}
		return property;
	}

	/**
	 * Return an instance of the model built from `data`.
	 *
	 * The default implementation simply passes all `data` as the `spec` to
	 * the builder.
	 *
	 * If no data is passed, simply return null.
	 *
	 * @param {{}} data - literal object passed to the builder.
	 * @param {repositoryContext} context - Contextual information
	 * passed to transformMaterializationData() methods as second argument.
	 * @return {model} Materialized instance
	 */
	static materialize(data, context) {
		if (isNull(data)) {
			return null;
		}

		// Create a copy of `data` so `transformMaterializationData()`
		// can freely rewrite the content of the object without
		// impacting the original object. This is only a shallow copy
		// so `transformMaterializationData()` are responsible for
		// cloning data values if necessary.
		const dataClone = { ...data };

		this.transformMaterializationData(dataClone, context);

		return this.buildFrom(dataClone);
	}

	static materializeAll(array = [], context) {
		return array.map((data) => this.materialize(data, context));
	}

	/**
	 * Builds and returns an instance of the class from materialization data.
	 */
	static buildFrom(data) {
		return new this(data);
	}

	/**
	 * Mutate `data` in place.
	 */
	static transformMaterializationData(_data) {}

	static _subclassResponsibility() {
		throw new Error("Subclass responsibility");
	}
}

export default Model;
